import { useMemo } from 'react';

interface SidebarNavParams {
  isAdmin: boolean;
  isCompliance: boolean;
  isFourOhOneKEnabled: boolean;
  isFourOhOneKOnly: boolean;
}

export interface SidebarNavItem {
  label: string;
  key: string;
  destination: string;
  icon: string;
  httpMethod?: 'delete';
}

export interface SidebarNavItems {
  primaryNavItems: SidebarNavItem[];
  adminItems: SidebarNavItem[];
  accountUtilityItems: SidebarNavItem[];
}

const useSidebarItems = ({
  isAdmin,
  isCompliance,
  isFourOhOneKEnabled,
  isFourOhOneKOnly,
}: SidebarNavParams): SidebarNavItems => {
  const primaryNavItems: SidebarNavItem[] = useMemo(() => {
    const nav: SidebarNavItem[] = [];

    if (!isFourOhOneKOnly) {
      nav.push({ label: 'Summary', key: 'summary', destination: '/summary', icon: 'Building' });
    }

    if (isFourOhOneKEnabled) {
      nav.push({ label: '401(k)s', key: 'four_oh_one_ks', destination: '/four_oh_one_ks', icon: 'NestEgg' });
    }

    nav.push(
      { label: 'Portfolios', key: 'portfolios', destination: '/portfolios', icon: 'Pie' },
      { label: 'Clients', key: 'clients', destination: '/clients', icon: 'People' },
      { label: 'Co-pilot', key: 'co_pilot', destination: '/co_pilot', icon: 'Compass' }
    );

    if (isFourOhOneKEnabled) {
      nav.push({ label: 'Client tours', key: 'client_tours', destination: '/tours', icon: 'Webinar' });
    }

    if (!isAdmin) {
      if (isCompliance) {
        nav.push({ label: 'Compliance', key: 'compliance', destination: '/compliance/households', icon: 'Lock' });
      }
      if (!isFourOhOneKOnly) {
        nav.push({
          label: 'Agreements',
          key: 'agreements',
          destination: '/client_agreement_signatures',
          icon: 'Document',
        });
      }
    }

    return nav;
  }, [isAdmin, isCompliance, isFourOhOneKOnly, isFourOhOneKEnabled]);

  const adminItems: SidebarNavItem[] = useMemo(() => {
    const nav: SidebarNavItem[] = [];

    if (isAdmin) {
      nav.push({ label: 'Reports', key: 'reports', destination: '/reports/fee_statements', icon: 'Presentation' });

      if (isCompliance) {
        nav.push({ label: 'Compliance', key: 'compliance', destination: '/compliance/households', icon: 'Lock' });
      }

      nav.push({ label: 'Client billing', key: 'billing_plans', destination: '/billing_plans', icon: 'DollarCircle' });

      if (!isFourOhOneKOnly) {
        nav.push({
          label: 'Agreements',
          key: 'agreements',
          destination: '/client_agreement_signatures',
          icon: 'Document',
        });
      }
    }

    return nav;
  }, [isAdmin, isCompliance, isFourOhOneKOnly]);

  const accountUtilityItems: SidebarNavItem[] = useMemo(() => {
    const nav: SidebarNavItem[] = [];

    const destination = isAdmin ? '/admin/firm' : '/security_settings';
    nav.push({ label: 'Settings', key: 'settings', destination, icon: 'Gear' });

    nav.push(
      { label: 'Support', key: 'support', destination: '/support', icon: 'QuestionMarkCircle' },
      { label: 'Log out', key: 'log_out', destination: '/advisors/sign_out', icon: 'Power', httpMethod: 'delete' }
    );

    return nav;
  }, [isAdmin]);

  return {
    primaryNavItems,
    adminItems,
    accountUtilityItems,
  };
};

export default useSidebarItems;
